import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Image as ImageIcon,
  Trash2,
  RefreshCw,
  Search,
  Copy,
  ExternalLink,
  HardDrive,
  Cloud,
  Loader2,
  CheckCircle2,
  Upload,
  Settings,
  Globe
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MediaLibrary = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [s3Configured, setS3Configured] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  
  // Branding state
  const [branding, setBranding] = useState({
    logo_url: '',
    favicon_url: '',
    site_name: 'CCA Montreal',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    youtube_url: ''
  });
  const [savingBranding, setSavingBranding] = useState(false);

  useEffect(() => {
    fetchMedia();
    fetchBranding();
  }, []);

  const fetchMedia = async () => {
    setLoading(true);
    try {      const response = await axios.get(`${API_URL}/media/list`);
      setMedia(response.data.files || []);
      setS3Configured(response.data.s3_configured);
    } catch (error) {
      toast.error('Failed to load media library');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings/branding`);
      setBranding(response.data);
    } catch (error) {
    }
  };
  
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);    let uploaded = 0;
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        await axios.post(`${API_URL}/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        });
        uploaded++;
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
    
    if (uploaded > 0) {
      fetchMedia();
    }
  };
  
  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await axios.put(`${API_URL}/settings/branding`, branding);
      toast.success('Branding settings saved');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleDelete = async (file) => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/media/${file.filename}?storage=${file.storage}`);
      toast.success(`Deleted ${file.filename}`);
      setMedia(prev => prev.filter(m => m.filename !== file.filename || m.storage !== file.storage));
      setDeleteConfirm(null);
    } catch (error) {
      toast.error('Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = async (url) => {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        setCopiedUrl(url);
        toast.success('URL copied to clipboard');
        setTimeout(() => setCopiedUrl(null), 2000);
        return;
      } catch (err) {
      }
    }
    
    // Fallback: Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        setCopiedUrl(url);
        toast.success('URL copied to clipboard');
        setTimeout(() => setCopiedUrl(null), 2000);
      } else {
        toast.error('Failed to copy URL. Please select and copy manually.');
      }
    } catch (err) {
      document.body.removeChild(textArea);
      toast.error('Failed to copy URL. Please select and copy manually.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredMedia = media.filter(file =>
    file.filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalSize = media.reduce((acc, file) => acc + file.size, 0);
  const s3Count = media.filter(f => f.storage === 's3').length;
  const localCount = media.filter(f => f.storage === 'local').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-2xl font-bold">Media Library</h1>
          <p className="text-muted-foreground">Manage uploaded images, files, and site branding</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            accept="image/*"
            multiple
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()} 
            disabled={uploading}
            className="bg-cameroon-green hover:bg-cameroon-green/90"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </>
            )}
          </Button>
          <Button variant="outline" onClick={fetchMedia} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="library" className="flex-1 sm:flex-none">
            <ImageIcon className="w-4 h-4 mr-2" />
            Library ({media.length})
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex-1 sm:flex-none">
            <Settings className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="library" className="space-y-6 mt-6">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ImageIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{media.length}</p>
                    <p className="text-xs text-muted-foreground">Total Files</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <HardDrive className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatFileSize(totalSize)}</p>
                    <p className="text-xs text-muted-foreground">Total Size</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Cloud className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{s3Count}</p>
                    <p className="text-xs text-muted-foreground">S3 Files</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <HardDrive className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{localCount}</p>
                    <p className="text-xs text-muted-foreground">Local Files</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* S3 Status */}
          {!s3Configured && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="p-4 flex items-center gap-3">
                <Cloud className="w-5 h-5 text-yellow-600" />
                <p className="text-sm text-yellow-800">
                  S3 is not configured. Files will be stored locally. Configure S3 in your environment for cloud storage.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search files..."
              className="pl-10"
            />
          </div>

          {/* Media Grid */}
          {filteredMedia.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredMedia.map((file) => (
                <div
                  key={`${file.storage}-${file.filename}`}
                  className="group relative aspect-square rounded-lg overflow-hidden bg-muted border hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedImage(file)}
                >
                  <img
                    src={file.url}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">Error</text></svg>';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      <p className="text-white text-xs truncate">{file.filename}</p>
                      <Badge variant={file.storage === 's3' ? 'default' : 'secondary'} className="text-[10px] mt-1">
                        {file.storage === 's3' ? <Cloud className="w-3 h-3 mr-1" /> : <HardDrive className="w-3 h-3 mr-1" />}
                        {file.storage.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <button
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(file);
                    }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? 'No files match your search' : 'No media files uploaded yet'}
                </p>
                <Button onClick={() => fileInputRef.current?.click()} className="bg-cameroon-green hover:bg-cameroon-green/90">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Your First Image
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="branding" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Site Branding
              </CardTitle>
              <CardDescription>
                Customize your application logo and favicon
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Site Name */}
              <div className="space-y-2">
                <Label>Site Name</Label>
                <Input
                  value={branding.site_name}
                  onChange={(e) => setBranding(prev => ({ ...prev, site_name: e.target.value }))}
                  placeholder="CCA Montreal"
                />
                <p className="text-xs text-muted-foreground">The name displayed in browser tabs and headers</p>
              </div>
              
              {/* Logo URL */}
              <div className="space-y-2">
                <Label>Logo URL</Label>
                <Input
                  value={branding.logo_url}
                  onChange={(e) => setBranding(prev => ({ ...prev, logo_url: e.target.value }))}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-muted-foreground">
                  Paste a URL or upload an image above and copy its URL
                </p>
                {branding.logo_url && (
                  <div className="mt-2 p-4 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                    <img 
                      src={branding.logo_url} 
                      alt="Logo preview" 
                      className="h-12 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              {/* Favicon URL */}
              <div className="space-y-2">
                <Label>Favicon URL</Label>
                <Input
                  value={branding.favicon_url}
                  onChange={(e) => setBranding(prev => ({ ...prev, favicon_url: e.target.value }))}
                  placeholder="https://example.com/favicon.ico"
                />
                <p className="text-xs text-muted-foreground">
                  The small icon displayed in browser tabs (recommended: 32x32 PNG or ICO)
                </p>
                {branding.favicon_url && (
                  <div className="mt-2 p-4 bg-muted rounded-lg flex items-center gap-3">
                    <p className="text-xs text-muted-foreground">Preview:</p>
                    <img 
                      src={branding.favicon_url} 
                      alt="Favicon preview" 
                      className="w-8 h-8 object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              {/* Divider */}
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-1">Social Media Links</h4>
                <p className="text-xs text-muted-foreground mb-4">These will appear on the homepage footer for visitors to follow your social accounts</p>
              </div>
              
              {/* Facebook URL */}
              <div className="space-y-2">
                <Label>Facebook Page URL</Label>
                <Input
                  value={branding.facebook_url || ''}
                  onChange={(e) => setBranding(prev => ({ ...prev, facebook_url: e.target.value }))}
                  placeholder="https://facebook.com/ccamontreal"
                  data-testid="branding-facebook-url"
                />
              </div>
              
              {/* Instagram URL */}
              <div className="space-y-2">
                <Label>Instagram URL</Label>
                <Input
                  value={branding.instagram_url || ''}
                  onChange={(e) => setBranding(prev => ({ ...prev, instagram_url: e.target.value }))}
                  placeholder="https://instagram.com/ccamontreal"
                  data-testid="branding-instagram-url"
                />
              </div>
              
              {/* TikTok URL */}
              <div className="space-y-2">
                <Label>TikTok URL</Label>
                <Input
                  value={branding.tiktok_url || ''}
                  onChange={(e) => setBranding(prev => ({ ...prev, tiktok_url: e.target.value }))}
                  placeholder="https://tiktok.com/@ccamontreal"
                  data-testid="branding-tiktok-url"
                />
              </div>
              
              {/* YouTube URL */}
              <div className="space-y-2">
                <Label>YouTube Channel URL</Label>
                <Input
                  value={branding.youtube_url || ''}
                  onChange={(e) => setBranding(prev => ({ ...prev, youtube_url: e.target.value }))}
                  placeholder="https://youtube.com/@ccamontreal"
                  data-testid="branding-youtube-url"
                />
              </div>
              
              <Button 
                onClick={handleSaveBranding} 
                disabled={savingBranding}
                className="bg-cameroon-green hover:bg-cameroon-green/90"
              >
                {savingBranding ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Save Branding Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
          
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <h4 className="font-semibold text-blue-800 mb-2">How to add a logo or favicon:</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Go to the Library tab and upload your image</li>
                <li>Click on the uploaded image to view details</li>
                <li>Copy the image URL</li>
                <li>Paste the URL in the Logo URL or Favicon URL field above</li>
                <li>Click Save Branding Settings</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{selectedImage?.filename}</DialogTitle>
            <DialogDescription>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant={selectedImage?.storage === 's3' ? 'default' : 'secondary'}>
                  {selectedImage?.storage === 's3' ? <Cloud className="w-3 h-3 mr-1" /> : <HardDrive className="w-3 h-3 mr-1" />}
                  {selectedImage?.storage?.toUpperCase()}
                </Badge>
                <Badge variant="outline">{formatFileSize(selectedImage?.size || 0)}</Badge>
                <Badge variant="outline">{formatDate(selectedImage?.last_modified || '')}</Badge>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          {selectedImage && (
            <div className="space-y-4">
              <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.filename}
                  className="w-full h-full object-contain"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Input value={selectedImage.url} readOnly className="flex-1 text-sm" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(selectedImage.url)}
                >
                  {copiedUrl === selectedImage.url ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(selectedImage.url, '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedImage(null)}>
              Close
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setDeleteConfirm(selectedImage);
                setSelectedImage(null);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this file?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteConfirm?.filename}</strong> from {deleteConfirm?.storage === 's3' ? 'S3' : 'local storage'}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteConfirm)}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MediaLibrary;
